/* eslint-disable filetree-structure/services -- a part of the `task`
service, imported by path rather than re-exported. The rule allows only
platform extensions beside the folder-named file. */
import Alerting from "$src/core/services/alerting";
import Car from "$src/core/services/car/car";
import buildCarCatalog, {
  isCarPlayable,
  toCarItem,
} from "$src/core/services/car/catalog/catalog";
import fetchCarCatalogData from "$src/core/services/car/catalog/catalog.data";
import type { CarCatalogData } from "$src/core/services/car/catalog/catalog.data";
import resolveCarPlayback from "$src/core/services/car/playback/playback.resolve";
import {
  getCarRuntime,
  isCarUiMounted,
  onCarRuntimeReady,
} from "$src/core/services/car/runtime/runtime";
import type { CarPlayRequest } from "$src/core/services/car/types";
import Logging from "$src/core/services/logging/logging";

/** The Android Auto service task, hook-free: a cold headless boot has no React tree. */

/** Must match the TASK_KEY constant of the native headless task service. */
export const ANDROID_CAR_TASK_KEY = "HyviewAndroidAuto";

const handlePlayRequest = (request: CarPlayRequest): void => {
  const runtime = getCarRuntime();
  if (!runtime) return;

  Logging.info("[car] tap", { id: request.id, type: request.type });
  resolveCarPlayback(runtime.api, request)
    .then((resolved) => {
      Logging.info("[car] resolved", {
        id: request.id,
        playable: resolved !== null,
      });
      if (!resolved) {
        Logging.warn("Car item has no playable sources", { ...request });
        Car.setPlayback(null).catch((error: unknown) => {
          Logging.error("Could not clear car playback", { error });
        });
        // Headless toasts would queue and pop up on the next app open.
        if (isCarUiMounted()) {
          Alerting.error(runtime.translate("ERROR/PLAYER_PAGE/TITLE"));
        }
        return;
      }
      Car.setPlayback(resolved).catch((error: unknown) => {
        Logging.error("Could not start car playback", { error });
      });
    })
    .catch((error: unknown) => {
      Logging.error("Could not resolve car playback", { error });
      if (isCarUiMounted()) {
        Alerting.error(runtime.translate("ERROR/PLAYER_PAGE/TITLE"));
      }
    });
};

let listenersRegistered = false;
const ensureCarListeners = (): void => {
  if (listenersRegistered) return;
  listenersRegistered = true;

  Car.addPlayRequestListener(handlePlayRequest);

  Car.addSearchRequestListener((request) => {
    const runtime = getCarRuntime();
    if (!runtime) {
      Car.setSearchResults({
        requestId: request.requestId,
        items: [],
      }).catch(Logging.error);
      return;
    }
    runtime.api
      .getSearchResults({ query: request.query })
      .then((results) =>
        Car.setSearchResults({
          requestId: request.requestId,
          items: results.items.filter(isCarPlayable).map(toCarItem),
        }),
      )
      .catch((error: unknown) => {
        Logging.error("Could not run car search", { error });
        return Car.setSearchResults({
          requestId: request.requestId,
          items: [],
        });
      });
  });

  Car.addConnectedListener(({ connected }) => {
    if (!connected) {
      Car.setPlayback(null).catch(Logging.error);
    }
  });

  // media3 shows the head unit its own error; this exists so failures (broken
  // DRM included) also reach Sentry.
  Car.addPlaybackErrorListener(({ message, errorCode, errorCodeName }) => {
    Logging.error("[car] native playback error", {
      message,
      errorCode,
      errorCodeName,
    });

    const runtime = getCarRuntime();
    if (runtime && isCarUiMounted()) {
      Alerting.error(runtime.translate("ERROR/PLAYER_PAGE/TITLE"));
    }
  });
};

let taskRunning: Promise<void> | null = null;
// A run arrived while one was in flight (fresher data): run once more after.
let rerunRequested = false;
// Warm-path data from the React tree; cold runs pass nothing and fetch.
let providedData: CarCatalogData | null = null;
// Unchanged data skips the publish and the head-unit refresh it triggers.
let publishedSnapshot: string | null = null;

async function runAndroidCarTask(data?: CarCatalogData): Promise<void> {
  const runtime = getCarRuntime();
  if (!runtime) {
    Logging.warn("Android Auto task skipped: runtime not ready yet");
    return undefined;
  }
  if (data) providedData = data;
  if (taskRunning) {
    rerunRequested = true;
    return taskRunning;
  }

  taskRunning = (async () => {
    const { api, translate } = runtime;

    ensureCarListeners();

    // The tap that woke the app may predate this JS runtime; drain it first.
    const pending = await Car.consumePendingPlay().catch((error: unknown) => {
      Logging.error("Could not read pending car play request", { error });
      return null;
    });
    if (pending) handlePlayRequest(pending);

    const provided = providedData;
    providedData = null;
    const { storefront, watchlist } =
      provided ?? (await fetchCarCatalogData(api, () => Car.isConnected()));

    // Don't overwrite the natively persisted cold-start catalog with an empty
    // one when nothing was fetched.
    if (!storefront && !watchlist) return;

    const catalog = buildCarCatalog(storefront, watchlist, {
      continueWatching: translate("CAR/CONTINUE_WATCHING"),
      watchlist: translate("WATCHLIST/TITLE"),
    });

    const snapshot = JSON.stringify(catalog.sections);
    if (snapshot === publishedSnapshot) return;

    await Car.setCatalog(catalog);
    publishedSnapshot = snapshot;
  })()
    .catch((error: unknown) => {
      Logging.error("Android Auto task failed", { error });
    })
    .finally(() => {
      taskRunning = null;
      if (rerunRequested) {
        rerunRequested = false;
        runAndroidCarTask().catch((error: unknown) => {
          Logging.error("Android Auto task failed", { error });
        });
      }
    });

  return taskRunning;
}

onCarRuntimeReady(() => {
  if (!Car.isSupported) return;
  // A play/search request can arrive before catalog data triggers a run.
  ensureCarListeners();
  // Connect can precede the runtime (cold headless boot): kick a refresh.
  if (Car.isConnected()) {
    runAndroidCarTask().catch((error: unknown) => {
      Logging.error("Android Auto task failed", { error });
    });
  }
});

export default runAndroidCarTask;
